import { ArrowLeft, Check } from 'lucide-react'
import { Box, Link, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { ProgressBar } from '../../components/ui'
import { ENTITY_SECTIONS, ENTITY_SECTION_LABELS, entityPercent, entitySectionProgress } from '../../lib/completion'
import { clientLocation, entityLocation } from '../flow'
import { MISC_PRIMARY } from '../../theme/onboardingTheme'
import { EntityStates } from './EntityStates'
import { EntityDrlps } from './EntityDrlps'
import { EntityIndividuals } from './EntityIndividuals'
import { EntityCredentials } from './EntityCredentials'

/** Per-entity container: the States → DRLP → Individual Licensees → Credentials loop repeats for every entity. */
export function EntityWorkflow() {
  const { state, location, currentEntity, goTo, canReach } = useOnboarding()
  if (location.kind !== 'entity' || !currentEntity) return null

  const entity = currentEntity
  const index = state.entities.findIndex((e) => e.id === entity.id)
  const pct = entityPercent(entity)

  return (
    <Box>
      <Box sx={{ mb: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', p: { xs: 2, sm: 2.5 } }}>
        <Link
          component="button"
          type="button"
          onClick={() => goTo(clientLocation('entityDashboard'))}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, fontWeight: 600 }}
        >
          <ArrowLeft size={14} /> Back to entity list
        </Link>
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b' }}>
              Entity {index + 1} of {state.entities.length}
            </Typography>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>{entity.name || 'Unnamed entity'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 220 }}>
            <Box sx={{ flex: 1 }}>
              <ProgressBar value={pct} />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{pct}% complete</Typography>
          </Box>
        </Box>
        <Box component="nav" aria-label="Entity sections" sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {ENTITY_SECTIONS.map((section, i) => {
            const active = section === location.section
            const done = entitySectionProgress(entity, section).score >= 1
            const reachable = active || canReach(entityLocation(entity.id, section))
            return (
              <Box
                key={section}
                component="button"
                type="button"
                onClick={() => goTo(entityLocation(entity.id, section))}
                disabled={!reachable}
                title={reachable ? undefined : 'Complete the earlier sections first'}
                aria-current={active ? 'step' : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  cursor: 'pointer',
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: active ? MISC_PRIMARY : '#e2e8f0',
                  bgcolor: active ? '#eff6ff' : '#ffffff',
                  color: active ? MISC_PRIMARY : '#334155',
                  px: 1.5,
                  py: 0.75,
                  fontSize: 13,
                  fontWeight: 600,
                  '&:disabled': { cursor: 'not-allowed', opacity: 0.5 },
                }}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    fontSize: 11,
                    bgcolor: done ? '#059669' : active ? MISC_PRIMARY : '#e2e8f0',
                    color: done || active ? '#ffffff' : '#475569',
                  }}
                >
                  {done ? <Check size={11} strokeWidth={3} /> : i + 1}
                </Box>
                {ENTITY_SECTION_LABELS[section]}
              </Box>
            )
          })}
        </Box>
      </Box>

      {location.section === 'states' ? <EntityStates entity={entity} /> : null}
      {location.section === 'drlp' ? <EntityDrlps entity={entity} /> : null}
      {location.section === 'individuals' ? <EntityIndividuals entity={entity} /> : null}
      {location.section === 'credentials' ? <EntityCredentials entity={entity} /> : null}
    </Box>
  )
}
